import {
  MarkdownForm,
  MarkdownESForm,
  ImageHotspotsForm,
  ImageHotspotsESForm,
  TutorialSlideForm,
  TutorialSlideESForm,
  QuestionWithoutCorrectAnswerForm,
  QuestionWithoutCorrectAnswerESForm,
  MultipleChoiceQuestionWithCorrectAnswerForm,
  MultipleChoiceQuestionWithCorrectAnswerESForm,
  FreeTextWithoutCorrectAnswerForm,
  FreeTextWithoutCorrectAnswerESForm,
  NumericWithoutCorrectAnswerForm,
  NumericWithoutCorrectAnswerESForm,
  markdownPreview,
  markdownESPreview,
  tutorialPreview,
  tutorialESPreview,
  questionPreview,
  questionESPreview,
} from "../components/ContentItem/ContentItemRenderHelpers";

const CONTENT_TYPES = {
  quiz: { id: "quiz", name: "Quiz", queryParam: "qt" },
  tutorial: { id: "tutorial", name: "Tutorial", queryParam: "qt" },
  survey: { id: "survey", name: "Survey", queryParam: "s" },
};

const CONTENT_TYPES_LIST = Object.values(CONTENT_TYPES);

const CONTENT_ITEM_TYPES = {
  imageHotspot: {
    id: "image-hotspot",
    name: "Image Hotspot",
    supportedContentTypes: [CONTENT_TYPES.quiz.id, CONTENT_TYPES.tutorial.id],
    isLegacy: false,
  },
  markdown: {
    id: "markdown",
    name: "Markdown",
    supportedContentTypes: [CONTENT_TYPES.quiz.id, CONTENT_TYPES.tutorial.id],
    isLegacy: false,
  },
  tutorialSlide: {
    id: "tutorial-slide",
    name: "Tutorial Slide",
    supportedContentTypes: [
      CONTENT_TYPES.quiz.id,
      CONTENT_TYPES.tutorial.id,
      CONTENT_TYPES.survey.id,
    ],
    isLegacy: true,
  },
  MultipleChoiceQuestionWithCorrectAnswer: {
    id: "multiple-choice-with-correct-answer",
    name: "Multiple Choice Question With Correct Answer",
    supportedContentTypes: [CONTENT_TYPES.quiz.id, CONTENT_TYPES.tutorial.id],
    isLegacy: false,
  },
  MutlipleChoiceQuestionWithoutCorrectAnswer: {
    id: "multiple-choice-without-correct-answer",
    name: "Multiple Choice Question Without Correct Answer",
    supportedContentTypes: [CONTENT_TYPES.survey.id],
    isLegacy: false,
  },
  FreeTextWithoutCorrectAnswer: {
    id: "free-text-without-correct-answer",
    name: "Free Text Without Correct Answer",
    supportedContentTypes: [CONTENT_TYPES.survey.id],
    isLegacy: false,
  },
  CheckboxWithoutCorrectAnswer: {
    id: "checkbox-without-correct-answer",
    name: "Checkbox Without Correct Answer",
    supportedContentTypes: [CONTENT_TYPES.survey.id],
    isLegacy: false,
  },
  NumericWithoutCorrectAnswer: {
    id: "numeric-without-correct-answer",
    name: "Numeric Without Correct Answer",
    supportedContentTypes: [CONTENT_TYPES.survey.id],
    isLegacy: false,
  },
};

const CONTENT_ITEM_TYPE_RENDERER = {
  [CONTENT_ITEM_TYPES.imageHotspot.id]: {
    renderFunction: ImageHotspotsForm,
    valuesToPick: ["hotspots", "markdown_i18n"],
    questionValuesToPick: [],
    previewField: markdownPreview,
    isPointsEnabled: false,
  },
  [CONTENT_ITEM_TYPES.markdown.id]: {
    renderFunction: MarkdownForm,
    valuesToPick: ["markdown_i18n"],
    questionValuesToPick: [],
    previewField: markdownPreview,
    isPointsEnabled: false,
  },
  [CONTENT_ITEM_TYPES.tutorialSlide.id]: {
    renderFunction: TutorialSlideForm,
    valuesToPick: ["slide_text_i18n"],
    questionValuesToPick: [],
    previewField: tutorialPreview,
    isPointsEnabled: false,
  },
  [CONTENT_ITEM_TYPES.MultipleChoiceQuestionWithCorrectAnswer.id]: {
    renderFunction: MultipleChoiceQuestionWithCorrectAnswerForm,
    valuesToPick: ["question", "answers"],
    questionValuesToPick: [],
    previewField: questionPreview,
    isPointsEnabled: true,
  },
  [CONTENT_ITEM_TYPES.MutlipleChoiceQuestionWithoutCorrectAnswer.id]: {
    renderFunction: QuestionWithoutCorrectAnswerForm,
    valuesToPick: ["question", "answers"],
    questionValuesToPick: ["response_is_required", "question_text_i18n"],
    previewField: questionPreview,
    isPointsEnabled: false,
  },
  [CONTENT_ITEM_TYPES.CheckboxWithoutCorrectAnswer.id]: {
    renderFunction: QuestionWithoutCorrectAnswerForm,
    valuesToPick: ["question", "answers"],
    questionValuesToPick: ["response_is_required", "question_text_i18n"],
    previewField: questionPreview,
    isPointsEnabled: false,
  },
  [CONTENT_ITEM_TYPES.FreeTextWithoutCorrectAnswer.id]: {
    renderFunction: FreeTextWithoutCorrectAnswerForm,
    valuesToPick: ["question"],
    questionValuesToPick: [
      "placeholder_text_i18n",
      "question_text_i18n",
      "response_is_required",
      "response_length",
    ],
    previewField: questionPreview,
    isPointsEnabled: false,
  },
  [CONTENT_ITEM_TYPES.NumericWithoutCorrectAnswer.id]: {
    renderFunction: NumericWithoutCorrectAnswerForm,
    valuesToPick: ["question"],
    previewField: questionPreview,
    questionValuesToPick: [
      "decimal_precision",
      "post_qualifier",
      "pre_qualifier",
      "question_text_i18n",
      "response_is_required",
      "validation_max_value",
      "validation_min_value",
    ],
    isPointsEnabled: false,
  },
};

const CONTENT_ITEM_TYPE_ES_RENDERER = {
  [CONTENT_ITEM_TYPES.imageHotspot.id]: {
    renderFunction: ImageHotspotsESForm,
    valuesToPick: ["hotspots", "markdown_i18n"],
    previewField: markdownESPreview,
  },
  [CONTENT_ITEM_TYPES.markdown.id]: {
    renderFunction: MarkdownESForm,
    valuesToPick: ["markdown_i18n"],
    previewField: markdownESPreview,
  },
  [CONTENT_ITEM_TYPES.tutorialSlide.id]: {
    renderFunction: TutorialSlideESForm,
    valuesToPick: ["slide_text_i18n"],
    previewField: tutorialESPreview,
  },
  [CONTENT_ITEM_TYPES.MultipleChoiceQuestionWithCorrectAnswer.id]: {
    renderFunction: MultipleChoiceQuestionWithCorrectAnswerESForm,
    valuesToPick: ["question", "answers"],
    previewField: questionESPreview,
  },
  [CONTENT_ITEM_TYPES.MutlipleChoiceQuestionWithoutCorrectAnswer.id]: {
    renderFunction: QuestionWithoutCorrectAnswerESForm,
    valuesToPick: ["question", "answers"],
    previewField: questionESPreview,
  },
  [CONTENT_ITEM_TYPES.CheckboxWithoutCorrectAnswer.id]: {
    renderFunction: QuestionWithoutCorrectAnswerESForm,
    valuesToPick: ["question", "answers"],
    previewField: questionESPreview,
  },
  [CONTENT_ITEM_TYPES.FreeTextWithoutCorrectAnswer.id]: {
    renderFunction: FreeTextWithoutCorrectAnswerESForm,
    valuesToPick: ["question"],
    previewField: questionESPreview,
  },
  [CONTENT_ITEM_TYPES.NumericWithoutCorrectAnswer.id]: {
    renderFunction: NumericWithoutCorrectAnswerESForm,
    valuesToPick: ["question"],
    previewField: questionESPreview,
  },
};

const ALL_CONTENT_ITEM_TYPE_LIST = Object.values(CONTENT_ITEM_TYPES);

const QUIZ_TUTORIAL_CONTENT_ITEM_TYPE_LIST = includeLegacyTypes =>
  Object.values(CONTENT_ITEM_TYPES).filter(
    contentItemType =>
      (contentItemType.supportedContentTypes.includes(CONTENT_TYPES.quiz.id) ||
        contentItemType.supportedContentTypes.includes(
          CONTENT_TYPES.tutorial.id
        )) &&
      (includeLegacyTypes ? true : !contentItemType.isLegacy)
  );

const QUIZ_TUTORIAL_CONTENT_ITEM_TYPES_BY_ID =
  QUIZ_TUTORIAL_CONTENT_ITEM_TYPE_LIST(true).map(
    contentItemType => contentItemType.id
  );

const SURVEY_CONTENT_ITEM_TYPE_LIST = includeLegacyTypes =>
  Object.values(CONTENT_ITEM_TYPES).filter(
    contentItemType =>
      contentItemType.supportedContentTypes.includes(CONTENT_TYPES.survey.id) &&
      (includeLegacyTypes ? true : !contentItemType.isLegacy)
  );

const SURVEY_CONTENT_ITEM_TYPES_BY_ID = SURVEY_CONTENT_ITEM_TYPE_LIST(true).map(
  contentItemType => contentItemType.id
);

const CONTENT_DIFFICULTY = [
  { id: "easy", name: "EASY" },
  { id: "intermediate", name: "INTERMEDIATE" },
  { id: "advanced", name: "DIFFICULT" },
];

const SITES = [
  { id: 2, name: "DE" },
  { id: 3, name: "NJ" },
  { id: 4, name: "Purple" },
  // { id: 5, name: "SMI_ONE_CIRCLE" },
  { id: 6, name: "SMI_CA" },
  { id: 7, name: "SMI_ND", param: "nd", hasContentPreview: true },
  { id: 14, name: "Kudzu", param: "kd", hasContentPreview: true },
];

const KUDZU_SITES = [
  { id: 4, name: "Kudzu", title: "KD", param: "kd", hasContentPreview: true },
];

const LANGUAGES = [
  { id: "en", name: "English" },
  { id: "es", name: "Spanish" },
];

const ADMIN_SITE = "pp-admin";

// A random and very out of bound value of ID used for displaying header
const HEADER_ID = 99445566;

export {
  ADMIN_SITE,
  ALL_CONTENT_ITEM_TYPE_LIST,
  CONTENT_ITEM_TYPES,
  CONTENT_TYPES,
  CONTENT_TYPES_LIST,
  CONTENT_ITEM_TYPE_RENDERER,
  CONTENT_ITEM_TYPE_ES_RENDERER,
  CONTENT_DIFFICULTY,
  HEADER_ID,
  KUDZU_SITES,
  SITES,
  SURVEY_CONTENT_ITEM_TYPE_LIST,
  SURVEY_CONTENT_ITEM_TYPES_BY_ID,
  QUIZ_TUTORIAL_CONTENT_ITEM_TYPE_LIST,
  QUIZ_TUTORIAL_CONTENT_ITEM_TYPES_BY_ID,
  LANGUAGES,
};
